import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { utils, writeFile } from "xlsx";
import ProspectTable from "./ProspectTable";
import { ownerSelector, prospectsSelector } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLists,
  fetchProspects,
  fetchUserOwnersWithWorkspaces,
  resolveDashboardScope,
  retryImportNow,
  cancelImportAutoRetry,
  fetchImportDailyQuota,
  fetchListImportHistory,
} from "../../utils";
import {
  setInCampaign,
  setListDescription,
  setListName,
  setProspects,
  setProspectsCount,
} from "../../store/slices/prospectsSlice";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LINKEDIN_URLS, SOCKET_URL, SOCKET_IO_CLIENT_OPTIONS } from "../../constants";
import { MdFileOpen } from "react-icons/md";
import axios from "../../utils/axios";
import Papa from "papaparse";
import { setAllProspectsList, setListId, setLists } from "../../store/slices/ownerSlice";
import { io, Socket } from "socket.io-client";
import { checkPermission } from "../../utils/index";
import Select from "../ui/Select";
import SearchInput from "../ui/SearchInput";
import { Button } from "../ui";
import AccountSelect from "../ui/AccountSelect";
import { DataTable, ServerTablePagination, TableRowActionMenu } from "../ui";
import { createList, deleteList, updateList } from "../../store/features/ownerActions";
import TagsHeaderMenu from "../tags/TagsHeaderMenu";
import CreateTagModal from "../tags/CreateTagModal";
import CreateListModal from "../Modals/CreateListModal";
import SelectListForImportModal from "../Modals/SelectListForImportModal";
import AddProspectsChooserModal from "../Modals/AddProspectsChooserModal";
import type { ColumnsType } from "rc-table/es/interface";

const EXPORT_ICON_URL = "/export_icon.svg";
const ADD_LEADS_ICON_URL = "/add_leads_icon.svg";
const INFO_ICON_URL = "/info_icon.svg";
const LINKEDIN_METHOD_ICON_URL = "/linkdin_icon.svg";
const CSV_METHOD_ICON_URL = "/csv_icon.svg";
const CHECK_HINT_ICON_URL = "/checklist_icon.svg";
const LINKEDIN_SEARCH_IMPORT_IMAGE_URL = "/new_img.png";
const LINKEDIN_SEARCH_IMPORT_IMAGE_INTRINSIC_WIDTH = 2000;
const LINKEDIN_SEARCH_IMPORT_IMAGE_INTRINSIC_HEIGHT = 760;
const LINKEDIN_GROUP_IMPORT_IMAGE_URL = "/grp_import.png";
const LINKEDIN_EVENTS_IMPORT_IMAGE_URL = "/event_import.png";
const LINKEDIN_SALES_NAVIGATOR_IMPORT_IMAGE_URL = "/sales_navigator.png";
const LINKEDIN_SINGLE_PROFILE_IMPORT_IMAGE_URL = "/single_profile_import.png";

// Import logs: intentionally enabled to debug sender/profile mismatches across envs (including staging).
// Scoped to import flows only to avoid noisy app-wide logging.
const IMPORT_DEBUG_ENABLED = true;

const safeImportDebugLog = (...args: unknown[]) => {
  if (!IMPORT_DEBUG_ENABLED) return;
  // eslint-disable-next-line no-console
  console.log("[import-debug]", ...args);
};

const safeImportDebugWarn = (...args: unknown[]) => {
  if (!IMPORT_DEBUG_ENABLED) return;
  // eslint-disable-next-line no-console
  console.warn("[import-debug]", ...args);
};


type Props = { utils: any; setShowExpiredAlert: any; view?: "list" | "detail" | "import" };
type ListImportProgress = {
  status?: "idle" | "in_progress" | "completed" | "failed" | "stopped";
  total?: number;
  scanned?: number;
  success?: number;
  duplicate?: number;
  false?: number;
  percentage?: number;
  message?: string;
  startedAt?: string | Date | null;
  updatedAt?: string | Date | null;
  completedAt?: string | Date | null;
};

type ListAutoRetry = {
  id?: string;
  status?: "idle" | "pending" | "running" | "cancelled" | "failed";
  runAt?: string | Date | null;
  remainingCount?: number;
  requestedCount?: number;
  attempt?: number;
  lastMessage?: string;
  linkedinUrl?: string;
};

const getListAutoRetryItems = (list: any): ListAutoRetry[] => {
  const queue = Array.isArray(list?.autoRetryQueue) ? list.autoRetryQueue : [];
  const active = queue.filter((item: any) =>
    ["pending", "running", "failed"].includes(String(item?.status || ""))
  );
  if (active.length) return active as ListAutoRetry[];
  const legacy = list?.autoRetry;
  if (
    legacy &&
    String(legacy?.linkedinUrl || "").trim() &&
    ["pending", "running", "failed"].includes(String(legacy?.status || ""))
  ) {
    return [{ id: "legacy", ...(legacy as ListAutoRetry) }];
  }
  return [];
};

const formatLastImport = (progress?: ListImportProgress) => {
  const raw = progress?.completedAt;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((startOfToday - startOfDate) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const shortenAutoRetryUrl = (url?: string) => {
  const raw = String(url || "").trim();
  if (!raw) return "LinkedIn URL";
  try {
    const parsed = new URL(raw);
    const path = parsed.pathname || "";
    const shown = `${parsed.hostname}${path}`;
    return shown.length > 52 ? `${shown.slice(0, 49)}…` : shown;
  } catch {
    return raw.length > 52 ? `${raw.slice(0, 49)}…` : raw;
  }
};

const formatAutoRetryCountdown = (runAt?: string | Date | null) => {
  if (!runAt) return "";
  const target = new Date(runAt).getTime();
  if (!Number.isFinite(target)) return "";
  const diffMs = target - Date.now();
  if (diffMs <= 0) return "soon";
  const totalMinutes = Math.ceil(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const IMPORT_SESSION_NOT_READY_MESSAGE =
  "LinkedIn session is not import-ready for this sender. Reconnect this account from a live LinkedIn browser session (Chrome extension) and try again.";

type ImportSocketErrorPayload = {
  code?: string;
  message?: string;
  recoverable?: boolean;
  shouldDisconnect?: boolean;
  stats?: {
    success?: number;
    duplicate?: number;
    false?: number;
    scan?: number;
  };
  requestedCount?: number;
};

const toUserFriendlyImportErrorMessage = (raw: unknown): string => {
  if (raw && typeof raw === "object") {
    const payload = raw as ImportSocketErrorPayload;
    const structuredMessage = String(payload?.message || "").trim();
    const code = String(payload?.code || "").trim();
    if (structuredMessage && code) {
      return structuredMessage;
    }
  }
  const text = String(
    raw && typeof raw === "object" && "message" in (raw as object)
      ? (raw as ImportSocketErrorPayload).message
      : raw || ""
  ).trim();
  if (!text) return "Unable to import prospects right now.";
  const lower = text.toLowerCase();
  if (
    lower.includes("err_fr_too_many_redirects") ||
    lower.includes("too many redirects") ||
    lower.includes("linkedin_session_redirect") ||
    lower.includes("session is not import-ready")
  ) {
    return IMPORT_SESSION_NOT_READY_MESSAGE;
  }
  if (
    lower.includes("unable to reach linkedin with this account") ||
    lower.includes("temporarily restricted") ||
    lower.includes("linkedin_account_restricted") ||
    lower.includes("linkedin_account_unreachable")
  ) {
    return "We are currently unable to reach LinkedIn with this account. Try logging into this account first in a browser; if everything looks fine, try again here.";
  }
  return text;
};

const ProspectMain = (props: Props) => {
  const { owner, listId, lists, currentWorkspace, currentUser, allProspectsList } =
    useSelector(ownerSelector);
  const [fetchOptions, setFetchOptions] = useState({
    page: 1,
    limit: 10,
  });
  const [listChanged, setListChanged] = useState(false);
  const { prospectsCount, listName, description } =
    useSelector(prospectsSelector);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showSelectListImportModal, setShowSelectListImportModal] = useState(false);
  const [showAddProspectsChooser, setShowAddProspectsChooser] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState<string | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadDescription, setLeadDescription] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedTagId, setSelectedTagId] = useState("all");
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const [isListSwitchLoading, setIsListSwitchLoading] = useState(false);
  const [isListsLoading, setIsListsLoading] = useState(() => {
    const cached = Array.isArray(lists)
      ? lists.filter((list: any) => list?.listName !== "All Prospects" && !list?.isDeleted)
      : [];
    return cached.length === 0;
  });

  const [searchQuery, setSearchQuery] = useState("");
  const activeImportSocketRef = useRef<Socket | null>(null);
  const [isStoppingImport, setIsStoppingImport] = useState(false);
  const lastImportStatusRef = useRef<string>("");
  const importFinishToastShownRef = useRef(false);
  const prospectFetchRequestRef = useRef(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listsSearchSeededRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isDetailView = props.view === "detail";
  const isImportView = props.view === "import";

  const modalSelfRef = useRef<HTMLDivElement>(null);
  const [linkedinUrl, setLinkedinUrl] = useState<string>("");
  const [numberToImport, setNumberToImport] = useState("0");
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [importAccounts, setImportAccounts] = useState<
    Array<{
      value: string;
      label: string;
      avatar: string;
      importDailyQuota?: {
        limit?: number;
        used?: number;
        remaining?: number;
        isLinkedinPremium?: boolean;
      } | null;
    }>
  >([]);
  const [selectedImportOwnerId, setSelectedImportOwnerId] = useState("");
  const [importDailyQuota, setImportDailyQuota] = useState<{
    limit: number;
    used: number;
    remaining: number;
    isLinkedinPremium?: boolean;
  } | null>(null);
  const [importHistoryItems, setImportHistoryItems] = useState<
    Array<{
      id: string;
      linkedinUrl: string;
      status: string;
      requestedCount: number;
      processedCount: number;
      finishedAt?: string | null;
      message?: string;
    }>
  >([]);
  const [importHistoryPage, setImportHistoryPage] = useState(1);
  const [importHistoryHasMore, setImportHistoryHasMore] = useState(false);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  const [isImportAccountsLoading, setIsImportAccountsLoading] = useState(false);
  const workspaceIdFromRedux =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace)?._id || "";
          } catch {
            return "";
          }
        })()
      : currentWorkspace?._id || "";

  const normalizeScopeId = (value: unknown): string => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object" && value !== null && "_id" in value) {
      return String((value as { _id?: unknown })._id || "").trim();
    }
    return String(value).trim();
  };

  const userIdHint =
    normalizeScopeId(currentUser?.userId as unknown) ||
    normalizeScopeId((owner as { userId?: unknown })?.userId);

  const workspaceHint =
    String(workspaceIdFromRedux || "").trim() ||
    normalizeScopeId(
      (owner as { workspaces?: Array<{ _id?: unknown }> })?.workspaces?.[0]?._id
    );

  const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
    userIdHint || undefined,
    workspaceHint || undefined
  );

  const workspaceId = String(resolvedWorkspaceId || "").trim();
  const userId = String(resolvedUserId || "").trim();
  const ownerId = owner?._id || "";
  const ownerNameHint = String((owner as { name?: unknown })?.name || "").trim();
  const ownerPublicIdentifierHint = String(
    (owner as { publicIdentifier?: unknown })?.publicIdentifier || ""
  ).trim();

  const resolveSelectedImportAccountMeta = useCallback(() => {
    const resolvedOwnerId = String(selectedImportOwnerId || ownerId || "").trim();
    const option = importAccounts.find((acc) => String(acc.value) === resolvedOwnerId);
    return {
      resolvedOwnerId,
      selectedImportOwnerId: String(selectedImportOwnerId || "").trim(),
      reduxOwnerId: String(ownerId || "").trim(),
      label: String(option?.label || "").trim(),
      avatar: String(option?.avatar || "").trim(),
      importAccountsCount: importAccounts.length,
    };
  }, [importAccounts, ownerId, selectedImportOwnerId]);

  const resolveLinkedinOwnerId = useCallback(async () => {
    if (ownerId) return ownerId;
    if (!userId || !workspaceId) return "";

    try {
      const response = await fetchUserOwnersWithWorkspaces(
        1,
        50,
        "",
        userId,
        workspaceId
      );
      const accounts: Array<Record<string, any>> = Array.isArray(response?.accounts)
        ? response.accounts
        : [];

      const tokenBackedOwner =
        accounts.find((account) => Boolean(account?.hasLinkedinToken)) || accounts[0];
      return String(tokenBackedOwner?._id || "").trim();
    } catch {
      return "";
    }
  }, [ownerId, userId, workspaceId]);

  useEffect(() => {
    const requestId = ++prospectFetchRequestRef.current;
    // Lists overview does not show prospects — skip fetch to avoid lag/flash on return.
    if (!isDetailView && !isImportView) {
      setIsListSwitchLoading(false);
      return;
    }
    if (listId) {
      // Prevent previous list rows from flashing while next list is loading.
      setIsListSwitchLoading(true);
      dispatch(setProspects([]));
      dispatch(setProspectsCount(0));
    }
    (async () => {
      try {
        if (listId) {
          const res = await fetchProspects(
            ownerId,
            listId,
            fetchOptions.page,
            fetchOptions.limit,
            userId,
            workspaceId,
            isDetailView ? { tagId: selectedTagId } : undefined
          );
          if (requestId !== prospectFetchRequestRef.current) return;
          // console.log(res);
          dispatch(setProspects(res.prospects));
          dispatch(setProspectsCount(res.totalProspects));
          dispatch(setListName(res.listName));
          dispatch(setListDescription(res.description));
          dispatch(setInCampaign(res.inActiveCampaign));
          setListChanged(true);
          setIsListSwitchLoading(false);
        }
      } catch (error) {
        if (requestId !== prospectFetchRequestRef.current) return;
        console.log(error);
        toast.error("An error occurred");
        setIsListSwitchLoading(false);
      }
    })();
    const handleClickOutside = (e: MouseEvent) => {
      if (
        modalSelfRef.current &&
        !modalSelfRef.current.contains(e.target as Node)
      ) {
        setShowExportOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    dispatch,
    fetchOptions.limit,
    fetchOptions.page,
    listId,
    ownerId,
    userId,
    workspaceId,
    isDetailView,
    isImportView,
    selectedTagId,
    uploading,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!workspaceId) {
          if (!cancelled) setIsListsLoading(false);
          return;
        }
        const hasCachedLists =
          Array.isArray(lists) &&
          lists.some(
            (list: any) => list?.listName !== "All Prospects" && !list?.isDeleted
          );
        // Only show skeleton when we have nothing to paint yet (avoids flash on back navigation).
        if (!hasCachedLists && !cancelled) setIsListsLoading(true);
        const res = await fetchLists(ownerId, workspaceId, userId);
        if (!cancelled) dispatch(setLists(res));
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) setIsListsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `lists` — refresh on scope change only; cached rows prevent skeleton flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, workspaceId, userId, dispatch]);

  useEffect(() => {
    if (isImportView && !selectedOption) {
      setSelectedOption("search");
    }
  }, [isImportView, selectedOption]);

  useEffect(() => {
    if (!isImportView) return;
    try {
      const reuseUrl = String(
        sessionStorage.getItem("leadseeder_reuse_import_url") || ""
      ).trim();
      if (!reuseUrl) return;
      sessionStorage.removeItem("leadseeder_reuse_import_url");
      setLinkedinUrl(reuseUrl);
      setSelectedOption(
        reuseUrl.includes("linkedin.com/in") ? "single" : "search"
      );
    } catch {
      /* ignore */
    }
  }, [isImportView]);

  useEffect(() => {
    if (!isImportView) return;
    if (!userId || !workspaceId) return;

    let isMounted = true;
    const loadImportAccounts = async () => {
      setIsImportAccountsLoading(true);
      try {
        const response = await fetchUserOwnersWithWorkspaces(1, 100, "", userId, workspaceId);
        const accounts: Array<Record<string, any>> = Array.isArray(response?.accounts)
          ? response.accounts
          : [];

        const activeAccounts = accounts.filter(
          (account) => Boolean(account?._id) && account?.isDisconnected !== true
        );

        const options = activeAccounts.map((account) => {
          const fallbackName =
            String(account?.email || account?.publicIdentifier || account?._id || "").trim();
          const name = String(account?.name || fallbackName || "LinkedIn Account").trim();
          const avatar = String(
            account?.picture ||
              account?.profileImage ||
              account?.profilePic ||
              account?.profilePicture ||
              ""
          ).trim();
          const quotaFromAccount = account?.importDailyQuota;
          const used = Number(
            quotaFromAccount?.used ?? account?.dailyUsage?.imports ?? 0
          );
          const limit = Number(
            quotaFromAccount?.limit ??
              (account?.isLinkedinPremium ? 1000 : 500)
          );
          return {
            value: String(account._id),
            label: name,
            avatar,
            importDailyQuota: {
              limit,
              used,
              remaining: Math.max(0, limit - used),
              isLinkedinPremium: Boolean(
                quotaFromAccount?.isLinkedinPremium ?? account?.isLinkedinPremium
              ),
            },
          };
        });

        if (!isMounted) return;
        setImportAccounts(options);

        if (options.length === 0) {
          setSelectedImportOwnerId("");
          return;
        }

        const ownerFromRedux = ownerId ? String(ownerId) : "";
        const hasReduxOwner = options.some((option) => option.value === ownerFromRedux);
        if (!selectedImportOwnerId) {
          setSelectedImportOwnerId(hasReduxOwner ? ownerFromRedux : options[0].value);
          return;
        }

        const stillValid = options.some((option) => option.value === selectedImportOwnerId);
        if (!stillValid) {
          setSelectedImportOwnerId(hasReduxOwner ? ownerFromRedux : options[0].value);
        }
      } catch {
        if (!isMounted) return;
        setImportAccounts([]);
        setSelectedImportOwnerId("");
      } finally {
        if (isMounted) {
          setIsImportAccountsLoading(false);
        }
      }
    };

    loadImportAccounts();
    return () => {
      isMounted = false;
    };
  }, [isImportView, ownerId, userId, workspaceId]);

  useEffect(() => {
    if (!isImportView) return;
    const resolvedOwnerId = String(selectedImportOwnerId || ownerId || "").trim();
    if (!resolvedOwnerId) {
      setImportDailyQuota(null);
      return;
    }
    const fromAccount = importAccounts.find(
      (acc) => String(acc.value) === resolvedOwnerId
    )?.importDailyQuota;
    if (fromAccount) {
      setImportDailyQuota({
        limit: Number(fromAccount.limit || 0),
        used: Number(fromAccount.used || 0),
        remaining: Number(fromAccount.remaining || 0),
        isLinkedinPremium: Boolean(fromAccount.isLinkedinPremium),
      });
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchImportDailyQuota({
          ownerId: resolvedOwnerId,
          userId,
          workspaceId,
        });
        if (cancelled) return;
        const q = data?.importDailyQuota || {};
        setImportDailyQuota({
          limit: Number(q.limit || 0),
          used: Number(q.used || 0),
          remaining: Number(q.remaining || 0),
          isLinkedinPremium: Boolean(q.isLinkedinPremium),
        });
      } catch {
        /* keep account-derived snapshot */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isImportView,
    selectedImportOwnerId,
    ownerId,
    userId,
    workspaceId,
    importAccounts,
  ]);

  const loadImportHistory = useCallback(
    async (page = 1, append = false) => {
      if (!listId || (!isDetailView && !isImportView)) return;
      setImportHistoryLoading(true);
      try {
        const data = await fetchListImportHistory({
          listId: String(listId),
          page,
          limit: 10,
          ownerId,
          userId,
          workspaceId,
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        setImportHistoryItems((prev) => (append ? [...prev, ...items] : items));
        setImportHistoryPage(Number(data?.page || page));
        setImportHistoryHasMore(Boolean(data?.hasMore));
      } catch {
        if (!append) setImportHistoryItems([]);
      } finally {
        setImportHistoryLoading(false);
      }
    },
    [isDetailView, isImportView, listId, ownerId, userId, workspaceId]
  );

  useEffect(() => {
    if (!isDetailView || !listId) return;
    void loadImportHistory(1, false);
  }, [isDetailView, listId, loadImportHistory]);

  useEffect(() => {
    const tagFromUrl = searchParams.get("tagId");
    setSelectedTagId(tagFromUrl || "all");
  }, [searchParams]);

  useEffect(() => {
    if (!isDetailView) return;
    const nextParams = new URLSearchParams(searchParams);
    if (selectedTagId === "all") {
      nextParams.delete("tagId");
    } else {
      nextParams.set("tagId", selectedTagId);
    }
    setSearchParams(nextParams, { replace: true });
  }, [isDetailView, searchParams, selectedTagId, setSearchParams]);

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    // console.log(file);

    if (file) {
      setIsFileSelected(true);
      setFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsFileSelected(true);
      setFile(e.target.files[0]);

      // console.log(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (uploading) return;
    if (!file) {
      toast.error("Please select a file to import");
      return;
    }

    if (file.type !== "text/csv") {
      toast.error("Please select a CSV file");
      return;
    }

    if (!listId) {
      toast.error("Please select a list to import to");
      return;
    }
    if (!userId || !workspaceId) {
      toast.error("Missing workspace or user context. Please refresh and try again.");
      return;
    }
    const resolvedOwnerId = selectedImportOwnerId || ownerId || (await resolveLinkedinOwnerId());
    if (!resolvedOwnerId) {
      toast.error("Please select a LinkedIn account before importing leads.");
      return;
    }
    if (importDailyQuota != null && Number(importDailyQuota.remaining || 0) <= 0) {
      toast.error(
        "Daily import limit reached. Limit resets with your daily quota — try again after reset."
      );
      return;
    }
    safeImportDebugLog("csv-import:start", {
      flow: "csv-import",
      listId,
      actor: {
        userId,
        workspaceId,
      },
      appOwnerContext: {
        reduxOwnerId: String(ownerId || "").trim(),
        reduxOwnerName: ownerNameHint,
        reduxOwnerPublicIdentifier: ownerPublicIdentifierHint,
      },
      sender: resolveSelectedImportAccountMeta(),
      workspaceId,
      userId,
      fileName: file?.name,
      fileType: file?.type,
      fileSize: file?.size,
      socketUrl: SOCKET_URL,
    });

    try {
      const allowed = await checkPermission(resolvedOwnerId);
      if (!allowed) {
        props.setShowExpiredAlert(true);
        return;
      }
      setUploading(true);
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = async (e) => {
        const text = e.target?.result as string;
        // console.log(text);
        const urls: string[] = [];
        const videoLinks: string[] = [];

        text.split(/(?:\r\n|\r|\n)/g).forEach((row)=>{
          row.split(",").forEach((url)=>{
            if(
              url !== undefined
            ){
              if(url.includes("linkedin.com/in/")){
                urls.push(url);
              }else if(url.includes("/share/d/")){
                videoLinks.push(url);
              }
            }
          })
          if(urls.length > videoLinks.length){
            videoLinks.push("")
          }
        })

        // console.log(urls);
        const prospects = urls
          .filter((url) => url !== "")
          .map((url) => url?.split("/in/")[1].split("/")[0]);
        if (prospects?.length === 0) {
          setUploading(false);
          setIsFileSelected(false);
          setFile(null);
          toast.error("This file does not contain any valid Linkedin URLs");
          return;
        }

        safeImportDebugLog("csv-import:parsed", {
          listId,
          sender: resolveSelectedImportAccountMeta(),
          prospectsCount: prospects.length,
          sampleVanities: prospects.slice(0, 5),
        });

        let sock = io(SOCKET_URL, { ...SOCKET_IO_CLIENT_OPTIONS });
        activeImportSocketRef.current = sock;
        importFinishToastShownRef.current = false;

        const finishCsvImport = (payload: any) => {
          const payloadListId = String(payload?.listId || "");
          if (payloadListId && listId && payloadListId !== String(listId)) return;
          sock.disconnect();
          activeImportSocketRef.current = null;
          props.utils.setVisibility("hidden");
          setIsFileSelected(false);
          setFile(null);
          setUploading(false);
          const status = String(payload?.status || "completed");
          const importedCount = Number(payload?.stats?.success || 0);
          const completionMessage = String(payload?.message || "").trim();
          if (!importFinishToastShownRef.current) {
            importFinishToastShownRef.current = true;
            if (status === "stopped") {
              toast.info("Import stopped");
            } else if (importedCount > 0) {
              toast.success("Leads added successfully");
            } else {
              toast.info(completionMessage || "Import completed (no leads were added).");
            }
          }
          if (listId) {
            navigate(`/prospects/${listId}`);
          }
        };

        const el = document.getElementById("stopSearching");
        el?.addEventListener("click", () => {
          const stopPayload = {
            listId,
            ownerId,
            userId,
            workspaceId,
          };
          // Prefer list-scoped stop so quota release still runs server-side.
          sock.emit("stopImportForList", stopPayload);
          sock.emit("stopImport", stopPayload);
          el.setAttribute("disabled", "");
          props.utils.setVisibility("hidden");
          // Keep socket briefly so stop events flush before disconnect.
          window.setTimeout(() => {
            try {
              sock.disconnect();
            } catch (_) {
              /* ignore */
            }
            if (activeImportSocketRef.current === sock) {
              activeImportSocketRef.current = null;
            }
          }, 400);
          setIsFileSelected(false);
          setFile(null);
          setUploading(false);
          setIsStoppingImport(false);
        });

        sock.on("importComplete", finishCsvImport);

        sock.on("importWarning", (data: any) => {
          const warningMessage = String(data?.message || "").trim();
          if (warningMessage) {
            toast.warning(warningMessage);
          }
        });

        sock.on("importinfo", (info: any) => {
          console.log("info", info);
          safeImportDebugLog("csv-import:importinfo", {
            flow: "csv-import",
            socketId: sock.id,
            sender: resolveSelectedImportAccountMeta(),
            listId,
            numberToimport: info?.numberToimport,
            stats: info?.stats,
            backendDebug: info?.debug,
          });
          props.utils.setimportSuccess(info.stats.success);
          props.utils.setimportDuplicates(info.stats.duplicate);
          props.utils.setimportScanned(info.stats.scan);
          props.utils.setimportFalsePositives(info.stats.false);
          const processedForPercent =
            Number(info?.stats?.success || 0) +
            Number(info?.stats?.duplicate || 0) +
            Number(info?.stats?.false || 0);
          props.utils.setimportPercent(
            Number(
              ((processedForPercent * 100) / Number(info?.numberToimport || 1)).toFixed(2)
            )
          );
        });

        sock.on("error", (data: any) => {
          safeImportDebugLog("csv-import:error", {
            flow: "csv-import",
            socketId: sock.id,
            sender: resolveSelectedImportAccountMeta(),
            listId,
            data,
          });
          sock.disconnect();
          activeImportSocketRef.current = null;
          setUploading(false);
          window.setTimeout(() => {
            props.utils.setVisibility("hidden");
          }, 1800);
          toast.error(
            toUserFriendlyImportErrorMessage(
              data?.message || data?.error?.message || data?.error
            )
          );
        });

        sock.on("connect_error", (error: any) => {
          safeImportDebugLog("csv-import:connect_error", {
            flow: "csv-import",
            socketId: sock.id,
            sender: resolveSelectedImportAccountMeta(),
            listId,
            message: error?.message,
          });
          sock.disconnect();
          activeImportSocketRef.current = null;
          setUploading(false);
          window.setTimeout(() => {
            props.utils.setVisibility("hidden");
          }, 1800);
          toast.error(error?.message || "Unable to connect to import service.");
        });

        props.utils.setimportSuccess(0);
        props.utils.setimportDuplicates(0);
        props.utils.setimportScanned(0);
        props.utils.setimportFalsePositives(0);
        props.utils.setimportPercent(Number(0));

        props.utils.setVisibility("");
        el?.removeAttribute("disabled");

        sock.on("connect", async () => {
          safeImportDebugLog("csv-import:socket_connected", {
            flow: "csv-import",
            socketId: sock.id,
            sender: resolveSelectedImportAccountMeta(),
            listId,
          });
          sock.emit("importCSV", {
            listId,
            prospects,
            ownerId: resolvedOwnerId,
            userId,
            workspaceId,
            socketId: sock.id,
            videoLinks: videoLinks,
          });
          safeImportDebugLog("csv-import:emit importCSV", {
            flow: "csv-import",
            socketId: sock.id,
            listId,
            sender: resolveSelectedImportAccountMeta(),
            prospectsCount: prospects.length,
          });
        });
      };
    } catch (error) {
      setUploading(false);
      console.log(error);
      toast.error("An error occurred");
    }
  };

  const handleLeadListSearch = useMemo(
    () =>
    _.debounce(async (query: string) => {
      try {
        if (!query.trim()) {
          if (!workspaceId) return;
          const allLists = await fetchLists(ownerId, workspaceId, userId);
          dispatch(setLists(allLists));
          return;
        }
        const { data } = await axios.post("/prospect/searchList", {
          userId,
          workspaceId,
          searchQuery: query,
        });
        dispatch(setLists(data));
      } catch (error) {
        console.log(error);
        toast.error("Error searching leads");
      }
    }, 350),
    [ownerId, workspaceId, userId, dispatch]
  );

  useEffect(() => {
    if (isDetailView || isImportView) return;
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
    if (!listsSearchSeededRef.current) {
      listsSearchSeededRef.current = true;
      if (!q.trim()) return;
    }
    void handleLeadListSearch(q);
  }, [handleLeadListSearch, isDetailView, isImportView, searchParams]);

  useEffect(() => {
    return () => {
      handleLeadListSearch.cancel();
    };
  }, [handleLeadListSearch]);

  const handleProspectsSearch = useMemo(
    () =>
      _.debounce(async (query: string) => {
        try {
          if (!listId) return;
          if (!query.trim()) {
            const res = await fetchProspects(
              ownerId,
              listId,
              1,
              fetchOptions.limit,
              userId,
              workspaceId,
              { tagId: selectedTagId }
            );
            dispatch(setProspects(res.prospects));
            dispatch(setProspectsCount(res.totalProspects));
            dispatch(setListName(res.listName));
            dispatch(setListDescription(res.description));
            return;
          }

        const { data } = await axios.post("/prospect/searchProspectsInList", {
            userId,
            workspaceId,
          listId,
          searchQuery: query,
        });
          dispatch(setProspects(data.prospects || []));
      } catch (error) {
        console.log(error);
          toast.error("Error searching prospects");
        }
      }, 350),
    [ownerId, listId, fetchOptions.page, fetchOptions.limit, userId, workspaceId, dispatch, selectedTagId]
  );

  useEffect(() => {
    return () => {
      handleProspectsSearch.cancel();
    };
  }, [handleProspectsSearch]);

  const handleExportToCSV = async () => {
    const loading = toast.loading("Exporting prospects...");
    try {
      const exportProspects = await fetchProspects(
        ownerId,
        listId,
        1,
        prospectsCount,
        userId,
        workspaceId,
        isDetailView ? { tagId: selectedTagId } : undefined
      );
      const csvData = exportProspects.prospects.map((prospect: any) => {
        return {
          // name: prospect.full_name,
          first_name: prospect.firstName,
          last_name: prospect.lastName,
          profileURL: LINKEDIN_URLS.profileBaseUrl + prospect.linkedinId,
          email: prospect.contactInfo?.emailAddress,
          phone:
            prospect.contactInfo?.phoneNumbers?.length > 0
              ? prospect.contactInfo?.phoneNumbers[0]?.number
              : "N/A",
          position: prospect.title,
          company: prospect.current_company_name,
          company_linkedin: prospect.company_linkedin_url,
          company_url: prospect.companyDetails?.url,
          industry: prospect.industry,
          premium: prospect.isPremium ? "Yes" : "No",
          skills: prospect.skills.map((skill: any) => skill.name).join(", "),
          location: prospect.locality,
          prospectList: listName,
          linkedinId: prospect.entityUrn,
        };
      });
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${listName}.csv`;
      a.click();

      toast.dismiss(loading);
      toast.success("Prospects exported successfully");
    } catch (error) {
      console.log(error);
      toast.dismiss(loading);
      toast.error("An error occurred");
    }
  };

  const handleExportToExcel = async () => {
    const loading = toast.loading("Exporting prospects...");

    try {
      const exportProspects = await fetchProspects(
        ownerId,
        listId,
        1,
        prospectsCount,
        userId,
        workspaceId,
        isDetailView ? { tagId: selectedTagId } : undefined
      );
      const excelData = exportProspects.prospects.map((prospect: any) => {
        return {
          // name: prospect.full_name,
          first_name: prospect.firstName,
          last_name: prospect.lastName,
          profileURL: LINKEDIN_URLS.profileBaseUrl + prospect.linkedinId,
          email: prospect.contactInfo?.emailAddress,
          phone:
            prospect.contactInfo?.phoneNumbers?.length > 0
              ? prospect.contactInfo?.phoneNumbers[0]?.number
              : "N/A",
          position: prospect.title,
          company: prospect.current_company_name,
          company_linkedin: prospect.company_linkedin_url,
          company_url: prospect.companyDetails?.url,
          industry: prospect.industry,
          premium: prospect.isPremium ? "Yes" : "No",
          skills: prospect.skills.map((skill: any) => skill.name).join(", "),
          location: prospect.locality,
          prospectList: listName,
          linkedinId: prospect.entityUrn,
        };
      });

      const ws = utils.json_to_sheet(excelData);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Prospects");

      writeFile(wb, `${listName}.xlsx`);
      toast.dismiss(loading);
    } catch (error) {
      console.log(error);
      toast.dismiss(loading);

      toast.error("An error occurred");
    }
  };

  const handleLinkedinSearchImport = async (countOverride?: number) => {
    const parsedCount = Number.parseInt(String(numberToImport || ""), 10) || 0;
    const effectiveCount = countOverride ?? parsedCount;
    if (!linkedinUrl || effectiveCount <= 0) {
      toast.error("Please provide a valid LinkedIn URL and number to import");
      return;
    }
    if (!listId) {
      toast.error("Please select a list to import to");
      return;
    }
    if (!userId || !workspaceId) {
      toast.error("Missing workspace or user context. Please refresh and try again.");
      return;
    }
    const resolvedOwnerId = selectedImportOwnerId || ownerId || (await resolveLinkedinOwnerId());
    if (!resolvedOwnerId) {
      toast.error("Please select a LinkedIn account before importing leads.");
      return;
    }

    let remainingToday = Number(importDailyQuota?.remaining);
    if (!Number.isFinite(remainingToday)) {
      try {
        const data = await fetchImportDailyQuota({
          ownerId: resolvedOwnerId,
          userId,
          workspaceId,
        });
        const q = data?.importDailyQuota || {};
        remainingToday = Number(q.remaining || 0);
        setImportDailyQuota({
          limit: Number(q.limit || 0),
          used: Number(q.used || 0),
          remaining: remainingToday,
          isLinkedinPremium: Boolean(q.isLinkedinPremium),
        });
      } catch {
        remainingToday = Infinity;
      }
    }
    if (remainingToday <= 0) {
      toast.error(
        "Daily import limit reached. Limit resets with your daily quota — try again after reset."
      );
      return;
    }
    if (effectiveCount > remainingToday) {
      toast.info(
        `Only ${remainingToday} lead${
          remainingToday === 1 ? "" : "s"
        } remaining in today's import limit. We'll import ${remainingToday} now and continue the rest after reset.`
      );
    }

    try {
      const vanityFromUrl = (() => {
        try {
          const u = new URL(linkedinUrl);
          const segments = String(u.pathname || "")
            .split("/")
            .map((s) => s.trim())
            .filter(Boolean);
          const inIndex = segments.findIndex((s) => s.toLowerCase() === "in");
          return inIndex >= 0 ? String(segments[inIndex + 1] || "").trim() : "";
        } catch {
          return "";
        }
      })();

      safeImportDebugLog("linkedin-import:start", {
        flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
        listId,
        actor: {
          userId,
          workspaceId,
        },
        appOwnerContext: {
          reduxOwnerId: String(ownerId || "").trim(),
          reduxOwnerName: ownerNameHint,
          reduxOwnerPublicIdentifier: ownerPublicIdentifierHint,
        },
        sender: resolveSelectedImportAccountMeta(),
        workspaceId,
        userId,
        linkedinUrl,
        vanityFromUrl,
        numberToImport: effectiveCount,
        socketUrl: SOCKET_URL,
      });

      let sock = io(SOCKET_URL, { ...SOCKET_IO_CLIENT_OPTIONS });
      activeImportSocketRef.current = sock;
      importFinishToastShownRef.current = false;

      const finishLinkedinImport = (payload: any) => {
        const payloadListId = String(payload?.listId || "");
        if (payloadListId && listId && payloadListId !== String(listId)) return;
        sock.disconnect();
        activeImportSocketRef.current = null;
        props.utils.setVisibility("hidden");
        const status = String(payload?.status || "completed");
        const importedCount = Number(payload?.stats?.success || 0);
        const completionMessage = String(payload?.message || "").trim();
        const completionCode = String(payload?.code || "").trim();
        if (!importFinishToastShownRef.current) {
          importFinishToastShownRef.current = true;
          if (status === "stopped") {
            toast.info("Import stopped");
          } else if (completionCode === "IMPORT_DAILY_LIMIT_REACHED") {
            toast.warning(
              completionMessage ||
                "Daily import limit reached. Remaining leads will continue after reset."
            );
          } else if (
            completionCode === "IMPORT_PARTIAL_COMPLETE" ||
            completionMessage.toLowerCase().includes("stopped early")
          ) {
            toast.warning(
              completionMessage ||
                "Import stopped early. We'll auto-retry in 24 hours — or use Retry now on the list."
            );
          } else if (importedCount > 0) {
            toast.success(completionMessage || "Leads added successfully");
          } else {
            toast.info(completionMessage || "Import completed (no leads were added).");
          }
        }
        if (listId) {
          navigate(`/prospects/${listId}`);
        }
      };

      const el = document.getElementById("stopSearching");
      el?.addEventListener("click", () => {
        const stopPayload = {
          listId,
          ownerId,
          userId,
          workspaceId,
        };
        sock.emit("stopImportForList", stopPayload);
        sock.emit("stopImport", stopPayload);
        el.setAttribute("disabled", "");
        props.utils.setVisibility("hidden");
        window.setTimeout(() => {
          try {
            sock.disconnect();
          } catch (_) {
            /* ignore */
          }
          if (activeImportSocketRef.current === sock) {
            activeImportSocketRef.current = null;
          }
        }, 400);
        setIsFileSelected(false);
        setFile(null);
        setUploading(false);
        setIsStoppingImport(false);
      });

      sock.on("importComplete", finishLinkedinImport);

      sock.on("importWarning", (data: any) => {
        const warningMessage = String(data?.message || "").trim();
        if (warningMessage) {
          toast.warning(warningMessage);
        }
      });

      sock.on("importinfo", (info: any) => {
        console.log("info", info);
        safeImportDebugLog("linkedin-import:importinfo", {
          flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
          socketId: sock.id,
          sender: resolveSelectedImportAccountMeta(),
          listId,
          linkedinUrl,
          numberToimport: info?.numberToimport,
          stats: info?.stats,
          backendDebug: info?.debug,
        });
        props.utils.setimportSuccess(info.stats.success);
        props.utils.setimportDuplicates(info.stats.duplicate);
        props.utils.setimportScanned(info.stats.scan);
        props.utils.setimportFalsePositives(info.stats.false);
        const processedForPercent =
          Number(info?.stats?.success || 0) +
          Number(info?.stats?.duplicate || 0) +
          Number(info?.stats?.false || 0);
        props.utils.setimportPercent(
          Number(
            ((processedForPercent * 100) / Number(info?.numberToimport || 1)).toFixed(2)
          )
        );
      });


      sock.on("error", (data: any) => {
        safeImportDebugLog("linkedin-import:error", {
          flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
          socketId: sock.id,
          sender: resolveSelectedImportAccountMeta(),
          listId,
          linkedinUrl,
          data,
        });
        sock.disconnect();
        activeImportSocketRef.current = null;
        window.setTimeout(() => {
        props.utils.setVisibility("hidden");
        }, 1800);
        toast.error(
          toUserFriendlyImportErrorMessage(
            data?.code
              ? {
                  code: data?.code,
                  message: data?.message || data?.error?.message || data?.error,
                }
              : data?.message || data?.error?.message || data?.error
          )
        );
      })

      sock.on("connect_error", (error: any) => {
        safeImportDebugLog("linkedin-import:connect_error", {
          flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
          socketId: sock.id,
          sender: resolveSelectedImportAccountMeta(),
          listId,
          linkedinUrl,
          message: error?.message,
        });
        sock.disconnect();
        activeImportSocketRef.current = null;
        window.setTimeout(() => {
          props.utils.setVisibility("hidden");
        }, 1800);
        toast.error(error?.message || "Unable to connect to import service.");
      });

      props.utils.setimportSuccess(0);
      props.utils.setimportDuplicates(0);
      props.utils.setimportScanned(0);
      props.utils.setimportFalsePositives(0);
      props.utils.setimportPercent(Number(0));

      props.utils.setVisibility("");
      el?.removeAttribute("disabled");
      if (listId) {
        navigate(`/prospects/${listId}`);
      }

      sock.on("connect", async () => {
        safeImportDebugLog("linkedin-import:socket_connected", {
          flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
          socketId: sock.id,
          sender: resolveSelectedImportAccountMeta(),
          listId,
          linkedinUrl,
        });
        sock.emit("importFromSearch", {
          listId,
          ownerId: resolvedOwnerId,
          userId,
          workspaceId,
          linkedinUrl,
          numberToImport: effectiveCount,
        });
        safeImportDebugLog("linkedin-import:emit importFromSearch", {
          flow: linkedinUrl.includes("linkedin.com/in") ? "linkedin-single-profile" : "linkedin-list",
          socketId: sock.id,
          listId,
          sender: resolveSelectedImportAccountMeta(),
          linkedinUrl,
          numberToImport: effectiveCount,
        });
      });

    } catch (error) {
      console.log(error);
      toast.error("An error occurred");
    }
  };

  const allLists = useMemo(
    () => (Array.isArray(lists) ? lists.filter((list: any) => !list.isDeleted) : []),
    [lists]
  );
  const leadLists = useMemo(
    () => allLists.filter((list: any) => list.listName !== "All Prospects"),
    [allLists]
  );
  const selectListImportOptions = useMemo(
    () =>
      leadLists.map((list: any) => ({
        _id: String(list?._id || ""),
        listName: list?.listName || "",
        prospectsCount: Array.isArray(list?.prospects) ? list.prospects.length : 0,
      })),
    [leadLists]
  );
  const currentList = useMemo(
    () => allLists.find((list: any) => String(list?._id || "") === String(listId || "")),
    [allLists, listId]
  );
  const currentListImportProgress = ((currentList as Record<string, any> | undefined)?.[
    "importProgress"
  ] || {}) as ListImportProgress;
  const currentListAutoRetries = useMemo(
    () => getListAutoRetryItems(currentList),
    [currentList]
  );
  const currentPendingAutoRetries = currentListAutoRetries.filter(
    (item) => String(item?.status || "") === "pending"
  );
  const currentRunningAutoRetries = currentListAutoRetries.filter(
    (item) => String(item?.status || "") === "running"
  );
  const hasCurrentPendingAutoRetry = currentPendingAutoRetries.length > 0;
  const hasCurrentRunningAutoRetry = currentRunningAutoRetries.length > 0;
  const isCurrentListImportRunning =
    String(currentListImportProgress?.status || "") === "in_progress" ||
    hasCurrentRunningAutoRetry;
  const currentListImportedCount = Number(currentListImportProgress?.success || 0);
  const currentListScannedCount = Number(currentListImportProgress?.scanned || 0);
  const currentListDuplicateCount = Number(currentListImportProgress?.duplicate || 0);
  const currentListFalseCount = Number(currentListImportProgress?.false || 0);
  const currentListImportTotal = Math.max(0, Number(currentListImportProgress?.total || 0));
  const currentListProcessedCountRaw =
    currentListImportedCount + currentListDuplicateCount + currentListFalseCount;
  const currentListProcessedCount =
    currentListImportTotal > 0
      ? Math.min(currentListImportTotal, currentListProcessedCountRaw)
      : currentListProcessedCountRaw;
  const isImportFinalizing =
    isCurrentListImportRunning &&
    currentListImportTotal > 0 &&
    currentListProcessedCount >= currentListImportTotal;
  const currentListImportPercent =
    currentListImportTotal > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Number(
              (
                Number(currentListImportProgress?.percentage) > 0
                  ? Number(currentListImportProgress?.percentage)
                  : (currentListProcessedCount * 100) / currentListImportTotal
              ).toFixed(0)
            )
          )
        )
      : 0;

  useEffect(() => {
    if (!isDetailView || !listId || !workspaceId) return;
    if (!isCurrentListImportRunning) return;

    const timer = window.setInterval(async () => {
      try {
        const listData = await fetchProspects(
          ownerId,
          listId,
          fetchOptions.page,
          fetchOptions.limit,
          userId,
          workspaceId,
          { tagId: selectedTagId }
        );
        dispatch(setProspects(listData.prospects || []));
        dispatch(setProspectsCount(listData.totalProspects || 0));
      } catch {
        // non-blocking realtime refresh
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    dispatch,
    fetchOptions.limit,
    fetchOptions.page,
    isCurrentListImportRunning,
    isDetailView,
    listId,
    ownerId,
    selectedTagId,
    userId,
    workspaceId,
  ]);

  const hasActiveListImports = useMemo(
    () =>
      allLists.some((list: any) => {
        const progressStatus = String(
          (list?.importProgress as ListImportProgress | undefined)?.status || ""
        );
        const retries = getListAutoRetryItems(list);
        return (
          progressStatus === "in_progress" ||
          retries.some((item) =>
            ["pending", "running"].includes(String(item?.status || ""))
          )
        );
      }),
    [allLists]
  );

  const [autoRetryBusyKey, setAutoRetryBusyKey] = useState("");

  const handleRetryImportNow = useCallback(
    async (targetListId: string, retryId?: string) => {
      const busyKey = `${targetListId}:${retryId || "all"}`;
      if (!targetListId || autoRetryBusyKey) return;
      setAutoRetryBusyKey(busyKey);
      try {
        await retryImportNow({
          listId: targetListId,
          retryId,
          ownerId,
          userId,
          workspaceId,
        });
        toast.success("Import retry started.");
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
      } catch (err: any) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Unable to start import retry.";
        toast.error(typeof msg === "string" ? msg : "Unable to start import retry.");
      } finally {
        setAutoRetryBusyKey("");
      }
    },
    [autoRetryBusyKey, dispatch, ownerId, userId, workspaceId]
  );

  const handleCancelImportAutoRetry = useCallback(
    async (targetListId: string, retryId?: string) => {
      const busyKey = `${targetListId}:${retryId || "all"}`;
      if (!targetListId || autoRetryBusyKey) return;
      setAutoRetryBusyKey(busyKey);
      try {
        await cancelImportAutoRetry({
          listId: targetListId,
          retryId,
          ownerId,
          userId,
          workspaceId,
        });
        toast.info("Auto-retry cancelled.");
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
      } catch (err: any) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Unable to cancel auto-retry.";
        toast.error(typeof msg === "string" ? msg : "Unable to cancel auto-retry.");
      } finally {
        setAutoRetryBusyKey("");
      }
    },
    [autoRetryBusyKey, dispatch, ownerId, userId, workspaceId]
  );
  const totalLeads = leadLists.length;
  const handleStopImport = useCallback(async () => {
    if (isStoppingImport) return;
    const activeSocket = activeImportSocketRef.current;
    setIsStoppingImport(true);
    try {
      if (activeSocket) {
        const stopPayload = {
          listId,
          ownerId,
          userId,
          workspaceId,
        };
        // Emit both events for compatibility with older/newer backend handlers.
        activeSocket.emit("stopImportForList", stopPayload);
        activeSocket.emit("stopImport", stopPayload);
        // Give socket a short window so packet is flushed before disconnect.
        await new Promise((resolve) => window.setTimeout(resolve, 220));
        activeSocket.disconnect();
        activeImportSocketRef.current = null;
      } else {
        const controlSocket = io(SOCKET_URL, { ...SOCKET_IO_CLIENT_OPTIONS });
        await new Promise<void>((resolve) => {
          let resolved = false;
          const safeResolve = () => {
            if (resolved) return;
            resolved = true;
            resolve();
          };
          const hardTimeout = window.setTimeout(() => {
            controlSocket.disconnect();
            safeResolve();
          }, 1800);
          controlSocket.on("connect", () => {
            const stopPayload = {
              listId,
              ownerId,
              userId,
              workspaceId,
            };
            controlSocket.emit("stopImportForList", stopPayload);
            controlSocket.emit("stopImport", stopPayload);
            window.setTimeout(() => {
              window.clearTimeout(hardTimeout);
              controlSocket.disconnect();
              safeResolve();
            }, 220);
          });
          controlSocket.on("connect_error", () => {
            window.clearTimeout(hardTimeout);
            controlSocket.disconnect();
            safeResolve();
          });
        });
      }
      props.utils.setVisibility("hidden");
      setUploading(false);
      setIsFileSelected(false);
      setFile(null);
      // Optimistically mark current list as stopped so card/status updates instantly.
      dispatch(
        setLists(
          (Array.isArray(lists) ? lists : []).map((list: any) => {
            if (String(list?._id || "") !== String(listId || "")) return list;
            const prevProgress = (list?.importProgress || {}) as Record<string, any>;
            return {
              ...list,
              importProgress: {
                ...prevProgress,
                status: "stopped",
                message: "Import stopped by user",
                updatedAt: new Date().toISOString(),
              },
            };
          })
        )
      );
      toast.info("Stopping import...");
      if (workspaceId) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
      }
    } catch {
      toast.error("Unable to stop import right now.");
    } finally {
      setIsStoppingImport(false);
    }
  }, [dispatch, isStoppingImport, listId, lists, ownerId, props.utils, userId, workspaceId]);

  const maxPage = Math.max(1, Math.ceil(totalLeads / fetchOptions.limit));
  const paginatedLeadLists = leadLists.slice(
    (fetchOptions.page - 1) * fetchOptions.limit,
    fetchOptions.page * fetchOptions.limit
  );

  useEffect(() => {
    if (isImportView) return;
    if (!workspaceId) return;
    if (!isDetailView && !hasActiveListImports) return;

    const refreshLists = async () => {
      try {
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
      } catch {
        // silent background refresh failure; user still has current snapshot
      }
    };

    // Run one immediate refresh so UI does not wait for first interval tick.
    refreshLists();

    const intervalMs = isDetailView ? 1000 : 2000;
    const timer = window.setInterval(refreshLists, intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [dispatch, hasActiveListImports, isDetailView, isImportView, ownerId, userId, workspaceId]);

  // Keep auto-retry countdown fresh on the import page.
  useEffect(() => {
    if (!isImportView || !workspaceId) return;
    if (!hasCurrentPendingAutoRetry && !hasCurrentRunningAutoRetry) return;

    const refreshLists = async () => {
      try {
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
      } catch {
        // silent
      }
    };
    refreshLists();
    const timer = window.setInterval(refreshLists, 2000);
    return () => window.clearInterval(timer);
  }, [
    dispatch,
    hasCurrentPendingAutoRetry,
    hasCurrentRunningAutoRetry,
    isImportView,
    ownerId,
    userId,
    workspaceId,
  ]);

  useEffect(() => {
    const nextStatus = String(currentListImportProgress?.status || "");
    const previousStatus = String(lastImportStatusRef.current || "");
    lastImportStatusRef.current = nextStatus;

    if (!isDetailView || !listId || !workspaceId) return;
    if (previousStatus !== "in_progress") return;
    if (!["completed", "failed", "stopped"].includes(nextStatus)) return;

    if (nextStatus === "stopped" && !importFinishToastShownRef.current) {
      importFinishToastShownRef.current = true;
      toast.info("Import stopped");
    }

    (async () => {
      try {
        const [refreshedListData, refreshedLists] = await Promise.all([
          fetchProspects(
            ownerId,
            listId,
            fetchOptions.page,
            fetchOptions.limit,
            userId,
            workspaceId,
            { tagId: selectedTagId }
          ),
          fetchLists(ownerId, workspaceId, userId),
        ]);
        dispatch(setProspects(refreshedListData.prospects || []));
        dispatch(setProspectsCount(refreshedListData.totalProspects || 0));
        dispatch(setListName(refreshedListData.listName || ""));
        dispatch(setListDescription(refreshedListData.description || ""));
        dispatch(setLists(refreshedLists));
      } catch {
        // non-blocking refresh fallback
      }
    })();
  }, [
    currentListImportProgress?.status,
    dispatch,
    fetchOptions.limit,
    fetchOptions.page,
    isDetailView,
    listId,
    ownerId,
    selectedTagId,
    userId,
    workspaceId,
  ]);

  useEffect(() => {
    if (isDetailView) return;
    if (fetchOptions.page > maxPage) {
      setFetchOptions((prev) => ({ ...prev, page: maxPage }));
    }
  }, [fetchOptions.limit, fetchOptions.page, isDetailView, totalLeads]);

  type LeadListRow = {
    key: string;
    _id: string;
    listName?: string;
    description?: string;
    inActiveCampaign?: boolean;
    prospects?: unknown[];
    importProgress?: ListImportProgress;
    autoRetry?: ListAutoRetry;
    autoRetryQueue?: ListAutoRetry[];
    importSourcesSummary?: string;
    activeCampaignName?: string;
    activeCampaignId?: string | null;
    activeCampaignCount?: number;
  };

  const openCreateListModal = () => {
    setEditingLeadId(null);
    setLeadName("");
    setLeadDescription("");
    setShowAddLeadModal(true);
  };

  const proceedToImportListPicker = () => {
    if (leadLists.length === 0) {
      openCreateListModal();
      return;
    }
    setShowSelectListImportModal(true);
  };

  const handleToolbarImport = () => {
    setShowAddProspectsChooser(true);
  };

  const handleSelectListForImport = (selectedId: string) => {
    if (!selectedId) return;
    setShowSelectListImportModal(false);
    dispatch(setListId(selectedId));
    navigate(`/prospects/${selectedId}/import`);
  };

  const leadListColumns: ColumnsType<LeadListRow> = [
    {
      title: "List",
      dataIndex: "listName",
      render: (value: string, record) => {
        const listProgress = (record?.importProgress || {}) as ListImportProgress;
        const retries = getListAutoRetryItems(record);
        const pendingRetries = retries.filter((item) => String(item?.status || "") === "pending");
        const runningRetries = retries.filter((item) => String(item?.status || "") === "running");
        const progressStatus = String(listProgress?.status || "");
        return (
          <div>
            <p className="truncate font-semibold text-dark-black">{value || ""}</p>
            {progressStatus === "in_progress" || runningRetries.length > 0 || pendingRetries.length > 0 ? (
              <div className="mt-1.5 flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
                {progressStatus === "in_progress" || runningRetries.length > 0 ? (
                  <p className="text-xs text-dark-grey">
                    {runningRetries.length > 0 ? "Retrying import" : "Import in progress"}
                  </p>
                ) : null}
                {pendingRetries.map((item) => {
                  const itemId = String(item.id || "");
                  const busyKey = `${record._id}:${itemId || "all"}`;
                  const busy = autoRetryBusyKey === busyKey;
                  const countdown = formatAutoRetryCountdown(item?.runAt);
                  const remaining = Math.max(0, Number(item?.remainingCount || 0));
                  return (
                    <div key={itemId || item.linkedinUrl} className="rounded-md bg-background-grey px-2 py-1.5">
                      <p className="text-xs text-dark-grey">
                        Auto-retry in {countdown || "…"}
                        {remaining > 0 ? ` · ${remaining} left` : ""}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={Boolean(autoRetryBusyKey)}
                          className="text-xs font-semibold text-brand-blue hover:underline disabled:opacity-50"
                          onClick={() => void handleRetryImportNow(record._id, itemId || undefined)}
                        >
                          {busy ? "Starting…" : "Retry now"}
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(autoRetryBusyKey)}
                          className="text-xs font-medium text-dark-grey hover:underline disabled:opacity-50"
                          onClick={() => void handleCancelImportAutoRetry(record._id, itemId || undefined)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Prospects",
      dataIndex: "prospects",
      width: 110,
      render: (_value, record) => {
        const listLeadCount = Array.isArray(record?.prospects) ? record.prospects.length : 0;
        return <p className="text-sm text-dark-black">{String(listLeadCount)}</p>;
      },
    },
    {
      title: "Import sources",
      key: "importSources",
      width: 200,
      render: (_value, record) => (
        <p className="truncate text-sm text-dark-grey" title={record.importSourcesSummary || undefined}>
          {record.importSourcesSummary || "—"}
        </p>
      ),
    },
    {
      title: "Active campaign",
      key: "activeCampaign",
      width: 200,
      render: (_value, record) => {
        const name = String(record.activeCampaignName || "").trim();
        if (!name) {
          return <p className="text-sm text-dark-grey">—</p>;
        }
        return (
          <p
            className="truncate text-sm font-medium text-dark-black"
            title={
              Number(record.activeCampaignCount || 0) > 1
                ? `${name} (${record.activeCampaignCount} campaigns)`
                : name
            }
          >
            {name}
          </p>
        );
      },
    },
    {
      title: "Last import",
      key: "lastImport",
      width: 130,
      render: (_value, record) => (
        <p className="text-sm text-dark-grey">{formatLastImport(record.importProgress)}</p>
      ),
    },
    {
      title: "",
      key: "importAction",
      width: 168,
      render: (_value, record) => (
        <div
          className="flex items-center justify-start gap-2"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outline"
            size="sm"
            className="h-10 !border-line bg-white px-3.5 font-semibold text-dark-blue hover:bg-paper"
            onClick={() => {
              if (!record?._id) return;
              navigate(`/prospects/${record._id}/import`);
            }}
          >
            Import
          </Button>
          <TableRowActionMenu
            onEdit={() => {
              setEditingLeadId(record._id);
              setLeadName(record.listName || "");
              setLeadDescription(record.description || "");
              setShowAddLeadModal(true);
            }}
            onDelete={() => {
              handleDeleteLead(record._id);
            }}
          />
        </div>
      ),
    },
  ];

  const leadListRows: LeadListRow[] = paginatedLeadLists.map((lead: any) => ({
    key: String(lead?._id || ""),
    _id: String(lead?._id || ""),
    listName: lead?.listName || "",
    description: lead?.description || "",
    inActiveCampaign: Boolean(lead?.inActiveCampaign),
    prospects: Array.isArray(lead?.prospects) ? lead.prospects : [],
    importProgress: lead?.importProgress || {},
    autoRetry: lead?.autoRetry || {},
    autoRetryQueue: Array.isArray(lead?.autoRetryQueue) ? lead.autoRetryQueue : [],
    importSourcesSummary: String(lead?.importSourcesSummary || "").trim(),
    activeCampaignName: String(lead?.activeCampaignName || "").trim(),
    activeCampaignId: lead?.activeCampaignId ? String(lead.activeCampaignId) : null,
    activeCampaignCount: Number(lead?.activeCampaignCount || 0),
  }));

  const handleCreateLead = async () => {
    if (!leadName.trim()) {
      toast.error("Lead name is required");
      return;
    }
    if (!workspaceId) {
      toast.error("Missing workspace context");
      return;
    }

    try {
      const result = await dispatch(
        createList({
          ownerId,
          userId,
          listName: leadName.trim(),
          description: leadDescription.trim(),
          workspaceId,
        }) as any
      );

      if (result?.meta?.requestStatus !== "fulfilled") {
        toast.error("Unable to create list");
        return;
      }

      const createdLeadId = result?.payload?._id;
      const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
      let mergedLists = refreshedLists;
      if (createdLeadId && !refreshedLists.some((l: any) => l._id === createdLeadId) && result?.payload) {
        // Backend list linkage can be slightly delayed; keep UI consistent immediately.
        mergedLists = [...refreshedLists, result.payload];
      }
      dispatch(setLists(mergedLists));
      const createdLead =
        mergedLists.find((l: any) => l._id === createdLeadId) ||
        mergedLists.find((l: any) => l.listName === leadName.trim());

      if (!createdLead?._id) {
        toast.error("List created, but unable to open it automatically.");
        return;
      }

      dispatch(setListId(createdLead._id));
      navigate(`/prospects/${createdLead._id}`);
      setFetchOptions((prev) => ({ ...prev, page: 1 }));

      // Immediately load the new lead list so the UI advances to it.
      const listData = await fetchProspects(
        ownerId,
        createdLead._id,
        1,
        fetchOptions.limit,
        userId,
        workspaceId
      );
      dispatch(setProspects(listData.prospects));
      dispatch(setProspectsCount(listData.totalProspects));
      dispatch(setListName(listData.listName));
      dispatch(setListDescription(listData.description));

      toast.success("List created successfully");
      setLeadName("");
      setLeadDescription("");
      setShowAddLeadModal(false);
    } catch (error) {
      console.log(error);
      toast.error("An error occurred");
    }
  };

  const resetLeadFormState = () => {
    setShowAddLeadModal(false);
    setEditingLeadId(null);
    setLeadName("");
    setLeadDescription("");
  };

  const handleSaveLead = async () => {
    if (!leadName.trim()) {
      toast.error("Lead name is required");
      return;
    }
    if (editingLeadId) {
      try {
        const res = await dispatch(
          updateList({
            listId: editingLeadId,
            listName: leadName.trim(),
            description: leadDescription.trim(),
            userId,
            workspaceId,
          }) as any
        );
        if (res?.meta?.requestStatus !== "fulfilled") {
          toast.error("Unable to update lead");
          return;
        }
        const updatedLead = res?.payload;
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        const mergedLists =
          updatedLead?._id && !refreshedLists.some((l: any) => l._id === updatedLead._id)
            ? refreshedLists.map((l: any) => (l._id === updatedLead._id ? { ...l, ...updatedLead } : l))
            : refreshedLists;
        dispatch(setLists(mergedLists));
        toast.success("Lead updated successfully");
        resetLeadFormState();
      } catch (error) {
        toast.error("Unable to update lead");
      }
      return;
    }
    await handleCreateLead();
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      const allListFromState = (lists || []).find(
        (list: any) =>
          typeof list?.listName === "string" &&
          list.listName.trim().toLowerCase() === "all prospects"
      );

      let resolvedAllProspectsListId =
        allProspectsList?._id && allProspectsList._id !== "all"
          ? allProspectsList._id
          : allListFromState?._id;

      // Fallback: refresh lists once before failing.
      if (!resolvedAllProspectsListId && workspaceId) {
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        dispatch(setLists(refreshedLists));
        const fetchedAllList = refreshedLists.find(
          (list: any) =>
            typeof list?.listName === "string" &&
            list.listName.trim().toLowerCase() === "all prospects"
        );
        if (fetchedAllList) {
          dispatch(setAllProspectsList(fetchedAllList));
          resolvedAllProspectsListId = fetchedAllList._id;
        }
      }

      const res = await dispatch(
        deleteList({
          listId: leadId,
          allProspectsListId: resolvedAllProspectsListId,
          userId,
          workspaceId,
        }) as any
      );
      if (res?.meta?.requestStatus !== "fulfilled") {
        toast.error("Unable to delete lead");
        return;
      }
      const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
      dispatch(setLists(refreshedLists));
      toast.success("Lead deleted successfully");
    } catch (error) {
      toast.error("Unable to delete lead");
    }
  };

  if (isImportView) {
    const activeOption = selectedOption || "search";
    const isSingleImport = activeOption === "single";
    const isCsvImport = activeOption === "csv";
    const isSearchImport = activeOption === "search";
    const importGuidanceImageSrc = isSingleImport
      ? LINKEDIN_SINGLE_PROFILE_IMPORT_IMAGE_URL
      : activeOption === "search"
        ? LINKEDIN_SEARCH_IMPORT_IMAGE_URL
        : activeOption === "group"
          ? LINKEDIN_GROUP_IMPORT_IMAGE_URL
          : activeOption === "events"
            ? LINKEDIN_EVENTS_IMPORT_IMAGE_URL
            : activeOption === "sales_navigator"
              ? LINKEDIN_SALES_NAVIGATOR_IMPORT_IMAGE_URL
              : LINKEDIN_SEARCH_IMPORT_IMAGE_URL;

    return (
      <section
        className={`flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden pt-6 font-inter ${
          isSearchImport ? "px-2" : "px-4"
        }`}
      >
        <h1 className="shrink-0 text-xl font-semibold text-dark-black">Leads</h1>
        {hasCurrentPendingAutoRetry || hasCurrentRunningAutoRetry ? (
          <div className="shrink-0 rounded-[12px] border border-light-grey bg-white p-4 shadow-sm">
            <p className="text-base font-semibold text-dark-black">
              {hasCurrentRunningAutoRetry && !hasCurrentPendingAutoRetry
                ? "Auto-retry in progress"
                : currentPendingAutoRetries.length > 1
                ? `${currentPendingAutoRetries.length} import auto-retries scheduled`
                : "Import auto-retry scheduled"}
            </p>
            <div className="mt-3 flex flex-col gap-3">
              {currentRunningAutoRetries.map((item) => (
                <div key={`run-${item.id || item.linkedinUrl}`} className="rounded-md bg-bg-blue/40 px-3 py-2">
                  <p className="text-sm text-dark-black">{shortenAutoRetryUrl(item.linkedinUrl)}</p>
                  <p className="text-xs text-dark-grey">
                    Retrying now
                    {Number(item.remainingCount || 0) > 0
                      ? ` · ${Number(item.remainingCount || 0)} left`
                      : ""}
                  </p>
                </div>
              ))}
              {currentPendingAutoRetries.map((item) => {
                const itemId = String(item.id || "");
                const busyKey = `${String(listId || "")}:${itemId || "all"}`;
                const busy = autoRetryBusyKey === busyKey;
                return (
                  <div key={`pend-${itemId || item.linkedinUrl}`} className="rounded-md bg-background-grey px-3 py-2">
                    <p className="text-sm text-dark-black">{shortenAutoRetryUrl(item.linkedinUrl)}</p>
                    <p className="text-xs text-dark-grey">
                      Auto-retry in {formatAutoRetryCountdown(item.runAt) || "…"}
                      {Number(item.remainingCount || 0) > 0
                        ? ` · ${Number(item.remainingCount || 0)} left`
                        : ""}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <Button
                        variant="primary"
                        size="sm"
                        isDisabled={Boolean(autoRetryBusyKey)}
                        onClick={() =>
                          void handleRetryImportNow(String(listId || ""), itemId || undefined)
                        }
                        className="h-9 min-w-[120px] rounded-[8px] px-4 text-sm"
                      >
                        {busy ? "Starting…" : "Retry now"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        isDisabled={Boolean(autoRetryBusyKey)}
                        onClick={() =>
                          void handleCancelImportAutoRetry(
                            String(listId || ""),
                            itemId || undefined
                          )
                        }
                        className="h-9 rounded-[8px] px-4 text-sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
                <div
          className={`grid min-h-0 flex-1 w-full grid-cols-1 ${
            isSearchImport
              ? "gap-3 lg:grid-cols-[200px_minmax(0,1fr)]"
              : "gap-4 lg:grid-cols-[240px_minmax(0,1fr)]"
          }`}
        >
          <div className="relative w-full shrink-0 rounded-[8px] bg-white lg:h-full lg:min-h-0">
            <p className="px-4 pt-4 text-md font-semibold leading-[20px] text-dark-black">Import methods</p>
            <div className="mt-4 hidden border-t border-light-grey lg:block" />
            <div className="flex gap-2 overflow-x-auto px-4 py-4 lg:flex-col lg:overflow-visible">
              {[
                { id: "search", label: "Import via Linkedin search", icon: LINKEDIN_METHOD_ICON_URL },
                { id: "group", label: "Import via Linkedin groups", icon: LINKEDIN_METHOD_ICON_URL },
                { id: "events", label: "Import via Linkedin Events", icon: LINKEDIN_METHOD_ICON_URL },
                { id: "sales_navigator", label: "Import via Sales Navigator", icon: LINKEDIN_METHOD_ICON_URL },
                { id: "single", label: "Import Single Profile", icon: LINKEDIN_METHOD_ICON_URL },
                { id: "csv", label: "Import from CSV", icon: CSV_METHOD_ICON_URL },
              ].map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedOption(method.id)}
                  className={`flex h-10 w-auto shrink-0 items-center whitespace-nowrap rounded-[6px] px-3 text-left text-sm font-medium leading-[18px] text-dark-black transition-colors lg:w-full lg:whitespace-normal ${
                    activeOption === method.id ? "bg-primary" : "bg-white"
                  }`}
                >
                  <span className="mr-2 flex h-5 w-5 items-center justify-center rounded-[8px] bg-white">
                    <img src={method.icon} alt="" className="h-[13px] w-[13px]" />
                  </span>
                  {method.label}
                </button>
              ))}
              </div>
                      </div>

          <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[8px] bg-white">
            <div className="flex h-12 shrink-0 items-center border-b border-light-grey px-4">
              <p className="text-[12px] font-medium leading-[15px] text-dark-black">
                {activeOption === "single"
                  ? "Import Single Profile"
                  : activeOption === "group"
                  ? "Import via Linkedin Groups"
                  : activeOption === "events"
                  ? "Import via Linkedin Events"
                  : activeOption === "sales_navigator"
                  ? "Import via Sales Navigator"
                  : activeOption === "csv"
                  ? "Import from CSV"
                  : "Import via Linkedin Search"}
              </p>
                    </div>

            <div
              className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${
                isSearchImport ? "px-2 py-4" : "p-4"
              }`}
            >
              <div className="mb-5">
                <AccountSelect
                  label="LinkedIn Account"
                  value={selectedImportOwnerId}
                  onChange={setSelectedImportOwnerId}
                  placeholder="Select LinkedIn account"
                  disabled={isImportAccountsLoading || importAccounts.length === 0}
                  options={
                    importAccounts.length > 0
                      ? importAccounts
                      : [{ value: "", label: "No LinkedIn accounts available", avatar: "" }]
                  }
                          />
                {importDailyQuota ? (
                  <p className="mt-2 text-xs text-dark-grey">
                    Daily import limit:{" "}
                    <span className="font-semibold text-dark-black">
                      {Number(importDailyQuota.used || 0)} /{" "}
                      {Number(importDailyQuota.limit || 0)}
                    </span>
                    {Number(importDailyQuota.remaining || 0) <= 0
                      ? " · Limit reached — resets with daily quota"
                      : ` · ${Number(importDailyQuota.remaining || 0)} remaining today`}
                    {importDailyQuota.isLinkedinPremium ? " · Premium" : ""}
                  </p>
                ) : null}
                        </div>
              {!isCsvImport ? (
                <>
                  {isSingleImport ? (
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <p className="mb-[10px] text-[12px] font-medium leading-[15px] text-dark-black">LinkedIn Profile URL</p>
                        <input
                          type="text"
                          placeholder="URL"
                          value={linkedinUrl}
                          onChange={(e) => setLinkedinUrl(e.target.value)}
                          className="h-9 w-full rounded-[4px] border border-light-grey px-[9px] text-[10px] leading-[12px] text-dark-black outline-none placeholder:text-dark-grey"
                        />
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          if (Number(importDailyQuota?.remaining || 0) <= 0) {
                            toast.error(
                              "Daily import limit reached. Limit resets with your daily quota."
                            );
                            return;
                          }
                          setNumberToImport("1");
                          handleLinkedinSearchImport(1);
                        }}
                        className="min-w-[120px] whitespace-nowrap rounded-[8px] px-4 text-sm"
                        iconSrc={ADD_LEADS_ICON_URL}
                        showBefore
                        iconClassName="h-4 w-4"
                        isDisabled={
                          importDailyQuota != null &&
                          Number(importDailyQuota.remaining || 0) <= 0
                        }
                      >
                        Add Leads
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-full">
                        <p className="mb-[10px] text-[12px] font-medium leading-[15px] text-dark-black">LinkedIn URL</p>
                        <input
                          type="text"
                          placeholder="URL"
                          value={linkedinUrl}
                          onChange={(e) => setLinkedinUrl(e.target.value)}
                          className="h-9 w-full rounded-[4px] border border-light-grey px-[9px] text-[10px] leading-[12px] text-dark-black outline-none placeholder:text-dark-grey"
                        />
                      </div>
                      <div className="w-full">
                        <p className="mb-[10px] text-[12px] font-medium leading-[15px] text-dark-black">Number to Import</p>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={numberToImport}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "");
                            setNumberToImport(digitsOnly);
                          }}
                          className="h-9 w-full rounded-[4px] border border-light-grey px-[9px] text-[10px] leading-[12px] text-dark-black outline-none"
                        />
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          if (Number(importDailyQuota?.remaining || 0) <= 0) {
                            toast.error(
                              "Daily import limit reached. Limit resets with your daily quota."
                            );
                            return;
                          }
                          handleLinkedinSearchImport();
                        }}
                        className="h-10 min-w-[120px] whitespace-nowrap rounded-[8px] px-4 text-sm"
                        iconSrc={ADD_LEADS_ICON_URL}
                        showBefore
                        iconClassName="h-4 w-4"
                        isDisabled={
                          importDailyQuota != null &&
                          Number(importDailyQuota.remaining || 0) <= 0
                        }
                      >
                        Add Leads
                      </Button>
                    </div>
                  )}

                  <div className={`space-y-[10px] ${isSingleImport ? "mt-4" : "mt-5"}`}>
                    {(isSingleImport
                      ? [
                          "Go to the Linkedin profile you want to import",
                          "Copy the profile URL from your browser",
                          "Paste it in the field above",
                          "Click import profile to add the Leads",
                        ]
                      : [
                          "Go to LinkedIn search results",
                          "Apply filters (e.g., location, industry, etc.) to refine your search",
                          "Copy the URL from your browser",
                          "Paste it in the field above",
                        ]
                    ).map((hint) => (
                      <div key={hint} className="flex items-center gap-2">
                        <img src={CHECK_HINT_ICON_URL} alt="" className="h-[18px] w-[18px]" />
                        <p className="text-[12px] font-medium leading-[15px] text-dark-black">{hint}</p>
                        </div>
                    ))}
                  </div>

                  <div
                    className={
                      isSearchImport
                        ? "mt-4 overflow-hidden border-y border-light-grey bg-primary/30"
                        : "mt-4 rounded-[8px] border border-light-grey bg-primary/30 p-2"
                    }
                  >
                    <img
                      key={importGuidanceImageSrc}
                      src={importGuidanceImageSrc}
                      alt="LinkedIn import guidance"
                      width={
                        isSearchImport
                          ? LINKEDIN_SEARCH_IMPORT_IMAGE_INTRINSIC_WIDTH
                          : undefined
                      }
                      height={
                        isSearchImport
                          ? LINKEDIN_SEARCH_IMPORT_IMAGE_INTRINSIC_HEIGHT
                          : undefined
                      }
                      className={
                        isSearchImport
                          ? "block h-auto w-full max-w-full object-contain"
                          : "mx-auto w-full max-w-full object-contain"
                      }
                    />
                  </div>
                </>
              ) : (
                  <div>
                  <p className="mb-4 text-[12px] font-medium leading-[15px] text-dark-black">Upload CSV File</p>
                  <div className="flex flex-col items-center gap-4 rounded-[8px] border border-dashed border-mainColor p-8">
                      <label
                      className="flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-2"
                        htmlFor="file"
                        onDragOver={(e) => handleDragOver(e)}
                        onDrop={(e) => handleDrop(e)}
                      >
                        {!isFileSelected ? (
                        <MdFileOpen size={42} className="text-mainColor" />
                        ) : (
                          <p className="text-[10px] leading-[12px] text-dark-black">{file?.name}</p>
                        )}
                      </label>
                      <input
                        onChange={(e) => handleFileChange(e)}
                        id="file"
                        name="file"
                        type="file"
                        accept=".csv"
                      className="hidden"
                    />
                    {isFileSelected ? (
                          <Button
                        variant="primary"
                        size="sm"
                        isDisabled={
                          uploading ||
                          (importDailyQuota != null &&
                            Number(importDailyQuota.remaining || 0) <= 0)
                        }
                        isLoading={uploading}
                          onClick={handleImport}
                        className="h-10 whitespace-nowrap rounded-[8px] px-4 text-sm"
                        >
                          {uploading ? "Importing..." : "Start Import"}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => document.getElementById("file")?.click()}
                        className="h-10 whitespace-nowrap rounded-[8px] px-4 text-sm"
                      >
                        Browse CSV
                      </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
          </div>
        </div>
      </section>
    );
  }

  if (!isDetailView) {
    return (
      <section className="noScroll h-full overflow-y-auto bg-paper px-4 py-6 sm:px-9 sm:py-9">
        <div className="mx-auto max-w-[1600px]">
          <header className="mb-6">
            <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">Lists</h1>
            <p className="mt-1 text-sm text-muted">
              Create a List first, then import a LinkedIn search URL or a CSV directly into that List.
            </p>
          </header>

          <div className="mb-4 flex flex-wrap items-center justify-end gap-2 pr-[18px]">
            <Button
              variant="outline"
              size="sm"
              className="h-10 !border-line bg-white px-3.5 font-semibold text-dark-blue hover:bg-paper"
              onClick={handleToolbarImport}
            >
              Import into a List
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="h-10 px-3.5 font-semibold text-dark-blue"
              onClick={openCreateListModal}
            >
              + Create List
            </Button>
          </div>

          <div className="v3-page-table overflow-hidden rounded-[10px] border border-line bg-white p-[18px]">
            <DataTable<LeadListRow>
              columns={leadListColumns}
              data={leadListRows}
              rowKey="key"
              emptyText={isListsLoading ? "" : "No lists yet"}
              tableLayout="fixed"
              loading={isListsLoading}
              skeletonRows={6}
              onRow={(record) => ({
                onClick: () => {
                  if (!record?._id) return;
                  dispatch(setListId(record._id));
                  navigate(`/prospects/${record._id}`);
                },
                className: "cursor-pointer",
              })}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px]">
            <p className="text-sm font-semibold text-dark-blue">List import flow</p>
            <p className="text-xs text-notice-text">
              Create or open a List → add a LinkedIn search URL or upload CSV →
              prospects automatically become members of that List.
            </p>
            <Button
              variant="primary"
              size="sm"
              className="ml-auto mr-1 h-10 px-3.5 font-semibold text-dark-blue"
              onClick={handleToolbarImport}
            >
              Import prospects
            </Button>
          </div>

          <div className="mt-3">
            <ServerTablePagination
              page={Math.max(0, fetchOptions.page - 1)}
              pageSize={fetchOptions.limit}
              total={totalLeads}
              rowsPerPageOptions={[10, 25, 50]}
              onPageChange={(nextPage) =>
                setFetchOptions((prev) => ({ ...prev, page: nextPage + 1 }))
              }
              onPageSizeChange={(nextSize) =>
                setFetchOptions((prev) => ({ ...prev, page: 1, limit: nextSize }))
              }
              loading={isListsLoading}
            />
          </div>
        </div>

        <CreateListModal
          isOpen={showAddLeadModal}
          onClose={resetLeadFormState}
          onSave={handleSaveLead}
          listName={leadName}
          onListNameChange={setLeadName}
          description={leadDescription}
          onDescriptionChange={setLeadDescription}
          title={editingLeadId ? "Edit List" : "Create List"}
          saveLabel={editingLeadId ? "Update" : "Save"}
        />
        <AddProspectsChooserModal
          open={showAddProspectsChooser}
          onClose={() => setShowAddProspectsChooser(false)}
          onFindProspects={() => {
            setShowAddProspectsChooser(false);
            navigate("/prospects/find");
          }}
          onImportProspects={() => {
            setShowAddProspectsChooser(false);
            proceedToImportListPicker();
          }}
        />
        <SelectListForImportModal
          isOpen={showSelectListImportModal}
          onClose={() => setShowSelectListImportModal(false)}
          lists={selectListImportOptions}
          onSelect={handleSelectListForImport}
        />
      </section>
    );
  }

  return (
    <section className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[8px] bg-background-grey px-4 pb-4 sm:px-6 sm:pb-6">
      <header className="shrink-0 py-6 pt-8">
        {isDetailView ? (
          <>
            <h1 className="text-xl font-semibold text-dark-black">{listName || "LinkedIn User"}</h1>
            <p className="mt-2 text-sm text-dark-grey">
              {description || "No description available for this list."}
            </p>
            {isCurrentListImportRunning &&
            String(currentListImportProgress?.status || "") === "in_progress" ? (
              <div className="mt-4 w-full max-w-[560px] rounded-[12px] border border-light-grey bg-primary p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-base font-semibold text-dark-blue">Import from LinkedIn Search Bar</p>
                  <Button
                    variant="primary"
                    size="sm"
                    isDisabled={isStoppingImport}
                    onClick={handleStopImport}
                    className="h-9 min-w-[130px] rounded-[8px] px-4 text-sm"
                  >
                    {isStoppingImport ? "Stopping..." : "Stop Importing"}
                  </Button>
                </div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-dark-black">
                    {isImportFinalizing
                      ? `Finalizing import (${currentListProcessedCount}/${currentListImportTotal})`
                      : `${currentListProcessedCount}/${currentListImportTotal} in progress`}
                  </p>
                  <p className="text-sm font-semibold text-dark-blue">
                    {currentListImportPercent.toFixed(0)}%
                  </p>
                </div>
                <p className="mb-2 text-xs text-dark-grey">
                  {isImportFinalizing
                    ? "Saving leads to your list..."
                    : `Imported ${currentListImportedCount} • Scanned ${currentListScannedCount}`}
                </p>
                <div className="h-3 w-full overflow-hidden rounded-full bg-white/70">
                  <div
                    className="h-full rounded-full bg-brand-blue transition-all duration-300"
                    style={{ width: `${currentListImportPercent}%` }}
                  />
                </div>
              </div>
            ) : null}
            {hasCurrentPendingAutoRetry || hasCurrentRunningAutoRetry ? (
              <div className="mt-4 w-full max-w-[560px] rounded-[12px] border border-light-grey bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-dark-black">
                  {hasCurrentRunningAutoRetry && !hasCurrentPendingAutoRetry
                    ? "Auto-retry in progress"
                    : currentPendingAutoRetries.length > 1
                    ? `${currentPendingAutoRetries.length} import auto-retries scheduled`
                    : "Import auto-retry scheduled"}
                </p>
                <div className="mt-3 flex flex-col gap-3">
                  {currentRunningAutoRetries.map((item) => (
                    <div
                      key={`run-${item.id || item.linkedinUrl}`}
                      className="rounded-md bg-bg-blue/40 px-3 py-2"
                    >
                      <p className="text-sm text-dark-black">
                        {shortenAutoRetryUrl(item.linkedinUrl)}
                      </p>
                      <p className="text-xs text-dark-grey">
                        Retrying now
                        {Number(item.remainingCount || 0) > 0
                          ? ` · ${Number(item.remainingCount || 0)} left`
                          : ""}
                      </p>
                    </div>
                  ))}
                  {currentPendingAutoRetries.map((item) => {
                    const itemId = String(item.id || "");
                    const busyKey = `${String(listId || "")}:${itemId || "all"}`;
                    const busy = autoRetryBusyKey === busyKey;
                    return (
                      <div
                        key={`pend-${itemId || item.linkedinUrl}`}
                        className="rounded-md bg-background-grey px-3 py-2"
                      >
                        <p className="text-sm text-dark-black">
                          {shortenAutoRetryUrl(item.linkedinUrl)}
                        </p>
                        <p className="text-xs text-dark-grey">
                          Auto-retry in {formatAutoRetryCountdown(item.runAt) || "…"}
                          {Number(item.remainingCount || 0) > 0
                            ? ` · ${Number(item.remainingCount || 0)} left`
                            : ""}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <Button
                            variant="primary"
                            size="sm"
                            isDisabled={Boolean(autoRetryBusyKey)}
                            onClick={() =>
                              void handleRetryImportNow(
                                String(listId || ""),
                                itemId || undefined
                              )
                            }
                            className="h-9 min-w-[120px] rounded-[8px] px-4 text-sm"
                          >
                            {busy ? "Starting…" : "Retry now"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            isDisabled={Boolean(autoRetryBusyKey)}
                            onClick={() =>
                              void handleCancelImportAutoRetry(
                                String(listId || ""),
                                itemId || undefined
                              )
                            }
                            className="h-9 rounded-[8px] px-4 text-sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="mt-4 w-full max-w-[560px] rounded-[12px] border border-light-grey bg-white p-4 shadow-sm">
              <p className="text-base font-semibold text-dark-black">Import URL history</p>
              <p className="mt-1 text-xs text-dark-grey">
                Recent LinkedIn URLs imported into this list. Click a URL to reuse it.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {importHistoryItems.length === 0 && !importHistoryLoading ? (
                  <p className="text-sm text-dark-grey">No import history yet.</p>
                ) : null}
                {importHistoryItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="rounded-md bg-background-grey px-3 py-2 text-left hover:bg-bg-blue/30"
                    onClick={() => {
                      if (!item.linkedinUrl) return;
                      try {
                        sessionStorage.setItem(
                          "leadseeder_reuse_import_url",
                          String(item.linkedinUrl)
                        );
                      } catch {
                        /* ignore */
                      }
                      navigate(`/prospects/${listId}/import`);
                    }}
                  >
                    <p className="truncate text-sm text-dark-black">
                      {item.linkedinUrl || "(no URL)"}
                    </p>
                    <p className="mt-1 text-xs text-dark-grey">
                      {item.finishedAt
                        ? new Date(item.finishedAt).toLocaleString()
                        : "—"}
                      {` · ${item.processedCount}/${item.requestedCount} processed`}
                      {item.status ? ` · ${item.status}` : ""}
                    </p>
                  </button>
                ))}
              </div>
              {importHistoryHasMore ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 h-9 rounded-[8px] px-4 text-sm"
                  isDisabled={importHistoryLoading}
                  onClick={() => void loadImportHistory(importHistoryPage + 1, true)}
                >
                  {importHistoryLoading ? "Loading…" : "See more"}
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <h1 className="text-h5 font-semibold tracking-tight text-dark-black">Lists</h1>
            <p className="mt-1 text-sm text-dark-grey">
              Create a List first, then import a LinkedIn search URL or a CSV directly into that List.
            </p>
          </>
        )}
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[8px] border border-light-grey bg-white">
        <div className="flex shrink-0 flex-col gap-4 border-b border-light-grey px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
            <div className="order-2 flex min-w-0 w-full flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:gap-5 md:order-1">
              <SearchInput
                id="leads-search-input-no-autofill"
                value={searchQuery}
                onChange={(value) => {
                  setSearchQuery(value);
                  if (isDetailView) {
                    setFetchOptions((prev) => ({ ...prev, page: 1 }));
                    handleProspectsSearch(value);
                  } else {
                    handleLeadListSearch(value);
                    setFetchOptions((prev) => ({ ...prev, page: 1 }));
                  }
                }}
                placeholder={isDetailView ? "Search" : "Search by name or description"}
                aria-label={isDetailView ? "Search leads" : "Search by name or description"}
                size="sm"
                containerClassName="relative min-w-0 w-full flex-1 sm:min-w-[200px] sm:max-w-[360px]"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                name="leads-search-input-no-autofill"
                style={{ backgroundImage: "none" }}
              />
              {isDetailView ? (
                <TagsHeaderMenu
                  onCreateNew={() => setCreateTagModalOpen(true)}
                  selectedTagId={selectedTagId}
                  onSelectTag={setSelectedTagId}
                />
              ) : null}
              {isDetailView ? (
                <div className="w-full shrink-0 sm:w-[200px]">
                  <Select
                    ariaLabel="Select status"
                    size="sm"
                    value={selectedStatus}
                    onChange={(value) => setSelectedStatus(value)}
                    options={[
                      { value: "all", label: "Select status" },
                      { value: "connected", label: "Connected" },
                      { value: "not_connected", label: "Not connected" },
                      { value: "has_email", label: "Has email" },
                      { value: "is_premium", label: "Is premium" },
                    ]}
                  />
                </div>
              ) : null}
              {isDetailView ? (
                <img src={INFO_ICON_URL} alt="" className="h-4 w-4 shrink-0 self-center sm:self-auto" />
              ) : null}
            </div>

            <div className="order-1 flex shrink-0 justify-end md:order-2">
              {isDetailView ? (
                <div className="relative flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    className="!text-black min-w-[104px] shrink-0 whitespace-nowrap"
                    onClick={() => setShowExportOptions((prev) => !prev)}
                    iconSrc={EXPORT_ICON_URL}
                    showBefore
                    iconClassName="h-4 w-4 shrink-0"
                  >
                    Export
                  </Button>
                  {showExportOptions && (
                    <div
                      ref={modalSelfRef}
                      className="absolute right-0 top-[calc(100%+6px)] z-50 flex min-w-[140px] flex-col gap-1 rounded-[8px] border border-light-grey bg-white p-2 shadow-md"
                    >
                      <button
                        onClick={handleExportToCSV}
                        className="rounded px-2 py-1 text-left text-xs text-dark-black hover:bg-primary"
                      >
                        Export to CSV
                      </button>
                      <button
                        onClick={handleExportToExcel}
                        className="rounded px-2 py-1 text-left text-xs text-dark-black hover:bg-primary"
                      >
                        Export to Excel
                      </button>
                    </div>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    className="!text-black min-w-[128px] shrink-0 whitespace-nowrap"
                onClick={() => {
                      if (!listId) return;
                      navigate(`/prospects/${listId}/import`);
                    }}
                    iconSrc={ADD_LEADS_ICON_URL}
                    showBefore
                    iconClassName="h-4 w-4 shrink-0"
                  >
                    Add Leads
                  </Button>
            </div>
              ) : (
                <div className="flex flex-wrap items-center justify-end gap-2 pr-[18px]">
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 whitespace-nowrap"
                    onClick={handleToolbarImport}
                  >
                    Import into a List
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="!text-black shrink-0 whitespace-nowrap"
                    onClick={openCreateListModal}
                  >
                    + Create List
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {isDetailView ? (
            <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
              <ProspectTable
                listChanged={listChanged}
                selectedStatus={selectedStatus}
                selectedTagId={selectedTagId}
                isListSwitchLoading={isListSwitchLoading}
              />
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
                <DataTable<LeadListRow>
                  columns={leadListColumns}
                  data={leadListRows}
                  rowKey="key"
                  emptyText={isListsLoading ? "" : "No lists yet"}
                  tableLayout="fixed"
                  loading={isListsLoading}
                  skeletonRows={6}
                  onRow={(record) => ({
                    onClick: () => {
                      if (!record?._id) return;
                      dispatch(setListId(record._id));
                      navigate(`/prospects/${record._id}`);
                    },
                    className: "cursor-pointer",
                  })}
                />
              </div>

              <div className="px-4 py-3 sm:px-6">
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-[9px] border border-mainColor/20 bg-success-bg px-4 py-3">
                  <p className="text-sm font-semibold text-dark-black">List import flow</p>
                  <p className="text-xs text-dark-grey">
                    Create or open a List → add a LinkedIn search URL or upload CSV →
                    prospects automatically become members of that List.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="ml-auto !text-black"
                    onClick={handleToolbarImport}
                  >
                    Import prospects
                  </Button>
                </div>
                <ServerTablePagination
                  page={Math.max(0, fetchOptions.page - 1)}
                  pageSize={fetchOptions.limit}
                  total={totalLeads}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={(nextPage) =>
                    setFetchOptions((prev) => ({ ...prev, page: nextPage + 1 }))
                  }
                  onPageSizeChange={(nextSize) =>
                    setFetchOptions((prev) => ({ ...prev, page: 1, limit: nextSize }))
                  }
                  loading={isListsLoading}
                />
          </div>
        </div>
      )}
        </div>
      </div>

      <CreateListModal
        isOpen={showAddLeadModal}
        onClose={resetLeadFormState}
        onSave={handleSaveLead}
        listName={leadName}
        onListNameChange={setLeadName}
        description={leadDescription}
        onDescriptionChange={setLeadDescription}
        title={editingLeadId ? "Edit List" : "Create List"}
        saveLabel={editingLeadId ? "Update" : "Save"}
      />

      <AddProspectsChooserModal
        open={showAddProspectsChooser}
        onClose={() => setShowAddProspectsChooser(false)}
        onFindProspects={() => {
          setShowAddProspectsChooser(false);
          navigate("/prospects/find");
        }}
        onImportProspects={() => {
          setShowAddProspectsChooser(false);
          proceedToImportListPicker();
        }}
      />
      <SelectListForImportModal
        isOpen={showSelectListImportModal}
        onClose={() => setShowSelectListImportModal(false)}
        lists={selectListImportOptions}
        onSelect={handleSelectListForImport}
      />

      {isDetailView ? (
        <CreateTagModal
          isOpen={createTagModalOpen}
          onClose={() => setCreateTagModalOpen(false)}
        />
      ) : null}
    </section>
  );
};

export default ProspectMain;